import axios from 'axios';
import { getConfig } from '../config/runtime-config';
import log from '../utils/logger';

// Global notification handler - will be set by App component
let showGlobalNotification = null;
export const setGlobalNotificationHandler = (fn) => { showGlobalNotification = fn; };

// Session expired handler - will be set by App component
let handleSessionExpired = null;
export const setSessionExpiredHandler = (fn) => { handleSessionExpired = fn; };

// Track whether the user is currently authenticated
let isUserAuthenticated = false;
export const setAuthenticatedState = (value) => { isUserAuthenticated = value; };

// Get base URL from runtime config, with fallback to localhost for development
const getBaseURL = () => {
  try {
    const config = getConfig();
    return config.apiUrl || 'http://localhost:5000';
  } catch (error) {
    // Config not initialized yet, use environment variable or localhost
    return process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }
};

const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

// Fetch a fresh CSRF token (tokens are single-use on the backend)
const fetchCSRFToken = async () => {
  const response = await axiosInstance.get('/csrf-token');
  return response.data.csrfToken;
};

let getAuthToken = null;
export const setAuthTokenGetter = (fn) => { getAuthToken = fn; };

// Add request interceptor to include CSRF token on mutating requests
axiosInstance.interceptors.request.use(async (config) => {
  if (getAuthToken) {
    try {
      const token = await getAuthToken();
      config.headers['Authorization'] = `Bearer ${token}`;
    } catch (e) {
      // unauthenticated — proceed without token
    }
  }
  if (config.method !== 'get' && !config.headers['x-csrf-token']) {
    const token = await fetchCSRFToken();
    config.headers['x-csrf-token'] = token;
  }
  return config;
});

// Add response interceptor to retry once on CSRF 403 errors and handle 401s
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors — only treat as session expiry when the user
    // was previously authenticated. During signup the user record doesn't exist in
    // the DB yet, so requests legitimately 401 even with a valid Auth0 token.
    if (error.response?.status === 401 && !isUserAuthenticated) {
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      if (showGlobalNotification) {
        showGlobalNotification('Your session has expired. Please sign in again.', 'error');
      }
      if (handleSessionExpired) {
        handleSessionExpired();
      }
      return Promise.reject(error);
    }

    // If we get a 403 with CSRF error and haven't retried yet, get a new token and retry
    if (
      error.response?.status === 403 &&
      error.response?.data?.error?.includes('CSRF') &&
      !originalRequest._csrfRetry
    ) {
      originalRequest.headers['x-csrf-token'] = await fetchCSRFToken();
      return axiosInstance(originalRequest);
    }

    return Promise.reject(error);
  }
);

export const endAuction = async (auctionId) => {
  try {
    const response = await axiosInstance.post(`/auctions/end-auction/${auctionId}`);
    return response.data;
  } catch (error) {
    log.error('Error ending auction:', error.response?.data || error.message);
    throw error;
  }
};

export default axiosInstance;